import React, { useMemo } from 'react';

import Modal from '@amzn/meridian/modal';

import { ColDef } from 'ag-grid-community';
import { BaseAgGrid } from 'src/components/BaseAgGrid';

import {
    convertRowToUSD,
    processComparisonRoyaltyData,
    processRawRoyaltyData
} from '../utils/executionRequestDataProcessing';

interface RoyaltyDetailsModalProps {
    open: boolean;
    onClose: () => void;
    selectedRowRoyalties: any[];
    showRawOutput?: boolean;
    convertToUSD?: boolean;
    conversionRates?: Map<string, Map<string, number>>;
    month?: string;
}

export const RoyaltyDetailsModal: React.FC<RoyaltyDetailsModalProps> = ({
    open,
    onClose,
    selectedRowRoyalties,
    showRawOutput = false,
    convertToUSD = false,
    conversionRates = new Map(),
    month = ''
}) => {
    const { rowData, versionInfo } = useMemo(
        () =>
            showRawOutput
                ? processRawRoyaltyData(selectedRowRoyalties)
                : processComparisonRoyaltyData(selectedRowRoyalties),
        [selectedRowRoyalties, showRawOutput]
    );

    const displayRowData = useMemo(() => {
        if (!convertToUSD || conversionRates.size === 0 || !month) return rowData;
        return rowData.map((row) => convertRowToUSD({ ...row, month }, conversionRates));
    }, [rowData, convertToUSD, conversionRates, month]);
    const columnDefs: ColDef[] = showRawOutput
        ? [
              { field: 'playType', headerName: 'Play Type', wrapText: true, autoHeight: true, wrapHeaderText: true },
              {
                  field: 'programType',
                  headerName: 'Program Type',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              { field: 'paperPlan', headerName: 'Paper Plan', wrapText: true, autoHeight: true, wrapHeaderText: true },
              {
                  field: 'currentRateValue',
                  headerName: 'Rate Value',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentRateCardCurrency',
                  headerName: 'Rate Card Currency',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentRateCardAmount',
                  headerName: 'Rate Card Amount',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentSettlementCurrency',
                  headerName: 'Settlement Currency',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentSettlementAmount',
                  headerName: 'Settlement Amount',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              }
          ]
        : [
              { field: 'playType', headerName: 'Play Type', wrapText: true, autoHeight: true, wrapHeaderText: true },
              {
                  field: 'programType',
                  headerName: 'Program Type',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              { field: 'paperPlan', headerName: 'Paper Plan', wrapText: true, autoHeight: true, wrapHeaderText: true },
              {
                  field: 'priorRateValue',
                  headerName: 'Prior Rate Value',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentRateValue',
                  headerName: 'Current Rate Value',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'priorRateCardCurrency',
                  headerName: 'Prior Rate Card Currency',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentRateCardCurrency',
                  headerName: 'Current Rate Card Currency',
                  wrapText: true,
                  wrapHeaderText: true
              },
              {
                  field: 'priorRateCardAmount',
                  headerName: 'Prior Rate Card Amount',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentRateCardAmount',
                  headerName: 'Current Rate Card Amount',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'priorSettlementCurrency',
                  headerName: 'Prior Settlement Currency',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentSettlementCurrency',
                  headerName: 'Current Settlement Currency',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'priorSettlementAmount',
                  headerName: 'Prior Settlement Amount',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              },
              {
                  field: 'currentSettlementAmount',
                  headerName: 'Current Settlement Amount',
                  wrapText: true,
                  autoHeight: true,
                  wrapHeaderText: true
              }
          ];

    return (
        <Modal open={open} onClose={onClose} title="Royalty Details">
            <div style={{ width: '95vw' }}>
                {selectedRowRoyalties.length > 0 ? (
                    <>
                        <BaseAgGrid
                            columnDefs={columnDefs}
                            rowData={displayRowData}
                            domLayout="autoHeight"
                            defaultColDef={{ wrapHeaderText: true, autoHeaderHeight: true }}
                        />
                        <div style={{ marginTop: '4px', fontSize: '11px', color: '#666', textAlign: 'left' }}>
                            <div>
                                {showRawOutput ? (
                                    <>Version ID: {versionInfo.currentVersion}</>
                                ) : (
                                    <>
                                        Prior Version ID: {versionInfo.priorVersion} | Current Version ID:{' '}
                                        {versionInfo.currentVersion}
                                    </>
                                )}
                            </div>
                            {!showRawOutput && (
                                <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                                    Rate card royalties may differ from settlement royalties due to currency differences
                                    in contract setup. Settlement amounts will be submitted for accruals/payments.
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <p>No royalty details available.</p>
                )}
            </div>
        </Modal>
    );
};
